/**
 * ConflictResolver - Conflict detection UI
 *
 * Key features:
 * - Promise-based resolve() method
 * - "Keep Mine" / "Use Theirs" modal
 * - XSS-safe content display (textContent, not innerHTML)
 *
 * Implementation: Task 5.0
 *
 * Usage:
 *   const resolver = new ConflictResolver()
 *
 *   // When SaveQueue receives 409 conflict
 *   const result = await resolver.resolve(
 *     localContent,
 *     serverContent,
 *     {
 *       blockId,
 *       editedBy: { id, name },
 *       editedAt: '2024-12-15T10:30:00Z'
 *     }
 *   )
 *
 *   if (result.action === 'keep_mine') {
 *     // Retry save with force flag or without lock version
 *   } else {
 *     // Apply server content to block
 *   }
 */

import { trackEditorEvent, EditorEvents } from './editor_analytics.js'
import { createLogger } from './logger.js'

// Phase 5: Shared logger with debug gating
const logger = createLogger('ConflictResolver')

// Resolution actions
export const ConflictAction = {
  KEEP_MINE: 'keep_mine',
  USE_THEIRS: 'use_theirs'
}

// Phase 10: Default timeout for conflict modal (5 minutes)
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000

export class ConflictResolver {
  /**
   * @param {Object} options - Configuration options
   * @param {number} options.timeoutMs - Auto-dismiss timeout in ms (default 5 minutes, 0 to disable)
   */
  constructor(options = {}) {
    // Currently displayed modal elements
    this.backdropEl = null
    this.modalEl = null

    // Bound event handlers for cleanup
    this.handleKeydown = this.handleKeydown.bind(this)
    this.currentResolve = null

    // Phase 10: Timeout configuration
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    this.timeoutId = null

    logger.log('Initialized with timeout:', this.timeoutMs)
  }

  /**
   * Resolve a conflict by showing a modal to the user
   * Phase 10: Added timeout to auto-dismiss after configurable period
   *
   * @param {Object} localContent - User's local changes
   * @param {Object} serverContent - Server's current content
   * @param {Object} blockInfo - Information about the conflict
   * @param {string} blockInfo.blockId - Block ID
   * @param {Object} blockInfo.editedBy - { id, name } of who edited the server version
   * @param {string} blockInfo.editedAt - ISO timestamp of server edit
   * @param {string} blockInfo.message - Optional conflict message
   * @returns {Promise<{action: string, content: Object, timedOut?: boolean}>} Resolution result
   */
  async resolve(localContent, serverContent, blockInfo = {}) {
    return new Promise((resolve) => {
      this.currentResolve = resolve

      // Show the conflict modal
      this.showModal(localContent, serverContent, blockInfo, (action, timedOut = false) => {
        // Phase 10: Clear timeout if user resolved manually
        this.clearTimeout()

        // Track analytics
        if (timedOut) {
          trackEditorEvent(EditorEvents.CONFLICT_TIMED_OUT, {
            blockId: blockInfo.blockId
          })
        } else if (action === ConflictAction.KEEP_MINE) {
          trackEditorEvent(EditorEvents.CONFLICT_RESOLVED_KEEP_MINE, {
            blockId: blockInfo.blockId
          })
        } else {
          trackEditorEvent(EditorEvents.CONFLICT_RESOLVED_USE_THEIRS, {
            blockId: blockInfo.blockId
          })
        }

        // Close modal
        this.hideModal()

        // Resolve promise with chosen content
        resolve({
          action,
          content: action === ConflictAction.KEEP_MINE ? localContent : serverContent,
          timedOut
        })
      })

      // Phase 10: Set up timeout to auto-dismiss (defaults to "use theirs" - safer choice)
      if (this.timeoutMs > 0) {
        this.timeoutId = setTimeout(() => {
          logger.warn('Conflict modal timed out, defaulting to use_theirs')
          if (this._currentOnResolve) {
            this._currentOnResolve(ConflictAction.USE_THEIRS, true)
          }
        }, this.timeoutMs)
      }
    })
  }

  /**
   * Phase 10: Clear the timeout timer
   * @private
   */
  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  /**
   * Show the conflict resolution modal
   * @private
   */
  showModal(localContent, serverContent, blockInfo, onResolve) {
    // Clean up any existing modal
    this.hideModal()

    // Create backdrop
    this.backdropEl = document.createElement('div')
    this.backdropEl.className = 'conflict-modal-backdrop'

    // Create modal
    this.modalEl = document.createElement('div')
    this.modalEl.className = 'conflict-modal'
    this.modalEl.setAttribute('role', 'alertdialog')
    this.modalEl.setAttribute('aria-modal', 'true')
    this.modalEl.setAttribute('aria-labelledby', 'conflict-modal-title')
    this.modalEl.setAttribute('aria-describedby', 'conflict-modal-desc')

    // Build editor info string (safe - XSS prevented)
    const editorName = blockInfo.editedBy?.name || 'another user'
    const editedAtStr = blockInfo.editedAt ?
      this.formatRelativeTime(blockInfo.editedAt) :
      'recently'

    // Create modal structure - note: no user content in innerHTML
    this.modalEl.innerHTML = `
      <div class="conflict-modal-icon">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 class="conflict-modal-title" id="conflict-modal-title">Edit Conflict</h3>
      <p class="conflict-modal-desc" id="conflict-modal-desc"></p>
      <div class="conflict-modal-info">
        <div class="conflict-modal-info-row">
          <span class="conflict-modal-info-label">Modified by</span>
          <span class="conflict-modal-info-value" id="conflict-editor-name"></span>
        </div>
        <div class="conflict-modal-info-row">
          <span class="conflict-modal-info-label">When</span>
          <span class="conflict-modal-info-value" id="conflict-edited-at"></span>
        </div>
      </div>
      <div class="conflict-modal-actions">
        <button type="button" class="conflict-modal-btn use-theirs" data-action="use_theirs">
          Use Their Version
        </button>
        <button type="button" class="conflict-modal-btn keep-mine" data-action="keep_mine">
          Keep My Changes
        </button>
      </div>
    `

    // Set text content safely (XSS prevention - Task 5.3)
    this.modalEl.querySelector('#conflict-modal-desc').textContent =
      'This block was modified while you were editing. Choose which version to keep.'
    this.modalEl.querySelector('#conflict-editor-name').textContent = editorName
    this.modalEl.querySelector('#conflict-edited-at').textContent = editedAtStr

    // Add to DOM
    document.body.appendChild(this.backdropEl)
    document.body.appendChild(this.modalEl)

    // Animate in
    requestAnimationFrame(() => {
      this.backdropEl.classList.add('visible')
      this.modalEl.classList.add('visible')
      // Focus "Keep My Changes" button (primary action)
      this.modalEl.querySelector('[data-action="keep_mine"]').focus()
    })

    // Handle button clicks (P1 fix: use closest() to handle clicks on inner elements)
    this.modalEl.addEventListener('click', (e) => {
      const actionEl = e.target.closest('[data-action]')
      const action = actionEl?.dataset.action
      if (action === ConflictAction.KEEP_MINE || action === ConflictAction.USE_THEIRS) {
        onResolve(action)
      }
    })

    // Handle backdrop click (dismiss as "use theirs" - safer choice)
    this.backdropEl.addEventListener('click', () => {
      onResolve(ConflictAction.USE_THEIRS)
    })

    // Handle Escape key
    document.addEventListener('keydown', this.handleKeydown)
    this._currentOnResolve = onResolve
  }

  /**
   * Handle keydown events for the modal
   * @private
   */
  handleKeydown(e) {
    if (e.key === 'Escape' && this._currentOnResolve) {
      // Escape defaults to "use theirs" (safer choice)
      this._currentOnResolve(ConflictAction.USE_THEIRS)
    }
  }

  /**
   * Hide and clean up the modal
   * @private
   */
  hideModal() {
    // Phase 10: Clear any pending timeout
    this.clearTimeout()

    document.removeEventListener('keydown', this.handleKeydown)
    this._currentOnResolve = null

    if (this.backdropEl) {
      this.backdropEl.classList.remove('visible')
    }
    if (this.modalEl) {
      this.modalEl.classList.remove('visible')
    }

    // Remove after animation
    setTimeout(() => {
      if (this.backdropEl) {
        this.backdropEl.remove()
        this.backdropEl = null
      }
      if (this.modalEl) {
        this.modalEl.remove()
        this.modalEl = null
      }
    }, 200)
  }

  /**
   * Format ISO timestamp as relative time
   * @private
   */
  formatRelativeTime(isoTimestamp) {
    try {
      const date = new Date(isoTimestamp)
      const now = new Date()
      const diffMs = now - date
      const diffSeconds = Math.floor(diffMs / 1000)
      const diffMinutes = Math.floor(diffSeconds / 60)
      const diffHours = Math.floor(diffMinutes / 60)

      if (diffMinutes < 1) {
        return 'just now'
      }
      if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
      }
      if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      }
      return date.toLocaleDateString()
    } catch {
      return 'recently'
    }
  }

  /**
   * Destroy the resolver and clean up
   */
  destroy() {
    this.hideModal()
    logger.log('Destroyed')
  }
}
